/**
 * AdminNoShows — Reporte de reservas no-show del período.
 *
 * KPIs: total, con pago completo, sin cargo (política amable), dinero perdido,
 *       comisión ahorrada.
 * Lista: filtros por agencia y fecha (fecha_salida).
 *
 * Regla de negocio:
 *   - CON PAGO TOTAL: cliente pagó todo → comisión legítima, movimiento normal.
 *   - SIN CARGO: cliente no pagó (saldo > 0) → CxC anulada + comisión pendiente anulada.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, CircularProgress, Alert, Tooltip, IconButton, Button,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReservaFinancieroModal from './ReservaFinancieroModal';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const fmt = (n) => new Intl.NumberFormat('es-CO').format(Math.round(Number(n) || 0));

/** $2.343.000 -> "$2,34M". Mismo formato que los tiles de Venta diaria, para
 *  que la cifra quepa siempre en una línea sin recortarse. */
function _fmtCompacto(n) {
  const v = Number(n || 0);
  if (v === 0) return '$0';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    const t = m >= 100 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, '');
    return `${sign}$${t.replace('.', ',')}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    const t = k >= 100 ? k.toFixed(0) : k.toFixed(1).replace(/\.?0+$/, '');
    return `${sign}$${t.replace('.', ',')}k`;
  }
  return `${sign}$${abs}`;
}

// Fecha de HOY en Bogotá, no en UTC. Con `toISOString()` el día cambiaba a
// las 7 p. m. hora local, así que después de esa hora el filtro apuntaba a
// mañana y la pantalla salía vacía justo cuando se cierra la operación.
const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

/** "2026-08-21T00:00:00" -> "21 ago". La fecha viene con hora del backend y
 *  sin formatear ocupaba una columna entera sin aportar nada. */
const fmtFecha = (v) => {
  if (!v) return '\u2014';
  const [a, m, d] = String(v).slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return String(v).slice(0, 10);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d} ${meses[m - 1]}`;
};
const menosDias = (d) => {
  const dt = new Date(Date.now() - d * 86400000);
  return dt.toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
};

export default function AdminNoShows() {
  // Por defecto: solo los pasadías que salieron HOY. Antes abría con los
  // últimos 30 días, que es un reporte histórico — no lo que se necesita
  // para revisar la operación del día.
  const [fechaDesde, setFechaDesde] = useState(hoy());
  const [fechaHasta, setFechaHasta] = useState(hoy());
  const [agenciaId, setAgenciaId] = useState('');
  const [buscar, setBuscar] = useState('');
  // El texto se aplica con retraso para no consultar en cada tecla.
  const [buscarAplicado, setBuscarAplicado] = useState('');
  const [agencias, setAgencias] = useState([]);
  const [data, setData] = useState({ kpis: null, items: [], fuera_de_rango: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reservaId, setReservaId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscarAplicado(buscar.trim()), 350);
    return () => clearTimeout(t);
  }, [buscar]);

  const fetchAgencias = async () => {
    try {
      const r = await fetch(`${API_BASE}/turismo/agencias?empresa_id=1`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        setAgencias(Array.isArray(j) ? j : (j.items || []));
      }
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ empresa_id: '1' });
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      if (buscarAplicado) params.set('buscar', buscarAplicado);
      if (agenciaId) params.set('agencia_id', agenciaId);
      const r = await fetch(`${API_BASE}/turismo/no-shows/dashboard?${params}`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData({ kpis: j.kpis, items: j.items || [], fuera_de_rango: j.fuera_de_rango || 0 });
    } catch (e) {
      setError(e.message || 'Error cargando no-shows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAgencias(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [fechaDesde, fechaHasta, agenciaId, buscarAplicado]);

  // Abre el rango a todo el histórico conservando la búsqueda. Se usa una
  // fecha de inicio muy anterior en vez de vaciar los campos: así los inputs
  // siguen mostrando exactamente lo que se está consultando.
  const ampliarRango = () => {
    setFechaDesde('2020-01-01');
    setFechaHasta(hoy());
  };

  const k = data.kpis || {};

  // `corto` recorta el rótulo para la tabla, donde cada píxel de ancho cuenta.
  // En las tarjetas de móvil hay espacio y se muestra completo.
  const chipRegla = (regla, corto = false) => {
    const base = { fontWeight: 700, height: 22, fontSize: 11,
                   '& .MuiChip-icon': { fontSize: 14 } };
    if (regla === 'con_pago_total') {
      return <Chip size="small" icon={<CheckCircleIcon />} label={corto ? 'Pagó' : 'Pagó completo'}
                   sx={{ ...base, bgcolor: '#dcfce7', color: '#166534' }} />;
    }
    if (regla === 'sin_cargo') {
      return <Chip size="small" icon={<BlockIcon />} label={corto ? 'Sin cargo' : 'Sin cargo (amable)'}
                   sx={{ ...base, bgcolor: '#fef3c7', color: '#92400e' }} />;
    }
    return <Chip size="small" icon={<WarningAmberIcon />} label={corto ? 'Pendiente' : 'Pendiente regularizar'}
                 sx={{ ...base, bgcolor: '#fee2e2', color: '#991b1b' }} />;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <EventBusyIcon sx={{ fontSize: 32, color: '#0d9488' }} />
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            No-shows del período
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            Reservas donde el turista no se presentó. Política Entretur aplicada silenciosamente al marcar no_show.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}
                 alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              type="date" label="Desde (fecha salida)" size="small"
              value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: 0, sm: 165 }, flex: { xs: 'none', sm: '0 0 auto' } }}
            />
            <TextField
              type="date" label="Hasta (fecha salida)" size="small"
              value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: 0, sm: 165 }, flex: { xs: 'none', sm: '0 0 auto' } }}
            />
            <TextField
              label="Buscar pasajero" size="small" placeholder="Nombre o código de reserva"
              value={buscar} onChange={(e) => setBuscar(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#94a3b8', mr: 0.75 }} />,
                endAdornment: buscar ? (
                  <IconButton size="small" onClick={() => setBuscar('')} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                ) : null,
              }}
              sx={{ minWidth: { xs: 0, sm: 210 }, flex: { xs: 'none', sm: 1 }, maxWidth: { sm: 300 } }}
            />
            <TextField
              select label="Agencia" size="small"
              value={agenciaId} onChange={(e) => setAgenciaId(e.target.value)}
              sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: 'none', sm: 1 }, maxWidth: { sm: 320 } }}
            >
              <MenuItem value="">Todas las agencias</MenuItem>
              {agencias.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {/* Cuando la búsqueda no da nada DENTRO del rango pero sí fuera, la
          pantalla lo dice y ofrece ampliar. El rango nunca se ignora en
          silencio: si mostrara resultados de julio con los campos diciendo
          "22 ago", la pantalla estaría mintiendo. */}
      {!loading && (data.fuera_de_rango || 0) > 0 && (
        <Alert severity="info" sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Button size="small" variant="contained" onClick={ampliarRango}
              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Ver todas
            </Button>
          }>
          {fechaDesde === fechaHasta
            ? <>Sin coincidencias el <b>{fmtFecha(fechaDesde)}</b>, </>
            : <>Sin coincidencias entre el <b>{fmtFecha(fechaDesde)}</b> y el <b>{fmtFecha(fechaHasta)}</b>, </>}
          pero hay <b>{data.fuera_de_rango}</b> en otras fechas.
        </Alert>
      )}

      {/* KPIs — mismo patrón que "Venta diaria" en Comercial: un solo Card
          contenedor con rejilla de tiles tintados, en vez de tarjetas sueltas
          con borde. Se replica de components/comercial/VentasDiaPage.jsx para
          que las dos pantallas se lean como el mismo sistema. */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center',
                     justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
              Resumen · {k.total || 0} {(k.total || 0) === 1 ? 'no-show' : 'no-shows'}
            </Typography>
            {(k.total || 0) > 0 && (
              <Chip size="small" label={`${Math.round(((k.sin_cargo || 0) / (k.total || 1)) * 100)}% sin cargo`}
                sx={{ bgcolor: '#fef3c7', color: '#a16207', fontWeight: 800, fontSize: 12 }} />
            )}
          </Box>

          {/* gridAutoRows 1fr + alignItems stretch: todos los tiles comparten
              alto aunque la rejilla se envuelva. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(5,1fr)' },
            gridAutoRows: '1fr', alignItems: 'stretch', gap: 1,
          }}>
            <Tile label="Total no-shows"    v={k.total || 0}           color="#334155" bg="#f1f5f9" conteo />
            <Tile label="Con pago completo" v={k.con_pago_total || 0}  color="#166534" bg="#dcfce7" conteo />
            <Tile label="Sin cargo (amable)" v={k.sin_cargo || 0}      color="#a16207" bg="#fef3c7" conteo />
            <Tile label="Dinero perdido"    v={k.dinero_perdido}       color="#b91c1c" bg="#fee2e2" />
            <Tile label="Comisión ahorrada" v={k.comision_ahorrada}    color="#0d9488" bg="#ccfbf1" />
          </Box>
        </CardContent>
      </Card>

      {(k.pendiente_regularizar || 0) > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Hay {k.pendiente_regularizar} reserva(s) marcada(s) como no-show que aún no tienen la política aplicada.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tabla — solo desde tablet. Nueve columnas en un teléfono obligan a
          hacer scroll lateral y a leer en zigzag; abajo se pinta la misma
          información como tarjetas. */}
      <TableContainer component={Paper}
        sx={{ borderRadius: 2, display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
        <Table size="small" sx={{
          '& td, & th': { px: 0.75, py: 0.75, fontSize: 12.5 },
          '& th': { fontSize: 11, whiteSpace: 'nowrap' },
        }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Reserva</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Destino</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Agencia</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Precio</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Perdido</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Com. anul.</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#64748b' }}>
                  Sin no-shows en el período.
                </TableCell>
              </TableRow>
            )}
            {!loading && data.items.map((it) => (
              <TableRow key={it.id} hover sx={{ cursor: 'pointer' }} onClick={() => setReservaId(it.id)}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><b>{it.codigo_reserva}</b></TableCell>
                <TableCell sx={{ maxWidth: 105, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.cliente}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFecha(it.fecha_salida)}</TableCell>
                <TableCell sx={{ maxWidth: 105, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.destino || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 105, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.agencia_nombre || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>${fmt(it.precio_total)}</TableCell>
                <TableCell align="right" sx={{ color: it.saldo_original_perdido > 0 ? '#991b1b' : '#64748b' }}>
                  ${fmt(it.saldo_original_perdido)}
                </TableCell>
                <TableCell align="right" sx={{ color: it.comision_original_anulada > 0 ? '#0d9488' : '#64748b' }}>
                  ${fmt(it.comision_original_anulada)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{chipRegla(it.regla, true)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Lista compacta para móvil y tablet angosta */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
        {loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        )}
        {!loading && data.items.length === 0 && (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 3, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              Sin no-shows en el período.
            </CardContent>
          </Card>
        )}
        {!loading && data.items.map((it) => (
          <Card key={it.id} onClick={() => setReservaId(it.id)}
                sx={{ borderRadius: 2, cursor: 'pointer' }}>
            <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                    {it.codigo_reserva}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#334155',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.cliente}
                  </Typography>
                </Box>
                {chipRegla(it.regla)}
              </Stack>

              <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.5 }}>
                {[fmtFecha(it.fecha_salida), it.destino, it.agencia_nombre].filter(Boolean).join(' · ')}
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                <DatoMovil rotulo="Precio" valor={`$${fmt(it.precio_total)}`} />
                <DatoMovil rotulo="Perdido" valor={`$${fmt(it.saldo_original_perdido)}`}
                           color={it.saldo_original_perdido > 0 ? '#991b1b' : '#64748b'} />
                <DatoMovil rotulo="Com. anulada" valor={`$${fmt(it.comision_original_anulada)}`}
                           color={it.comision_original_anulada > 0 ? '#0d9488' : '#64748b'} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {reservaId && (
        <ReservaFinancieroModal
          reservaId={reservaId}
          onClose={() => setReservaId(null)}
        />
      )}
    </Box>
  );
}

/** Par rótulo/valor de la tarjeta móvil. */
function DatoMovil({ rotulo, valor, color = '#0f172a' }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 9.5, color: '#94a3b8', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: .3 }}>
        {rotulo}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color,
                        fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </Typography>
    </Box>
  );
}

/**
 * Tile — calcado de VentasDiaPage.jsx. Fondo tintado, rótulo diminuto en
 * mayúsculas y valor en monoespaciada compacta. `conteo` lo usa para las
 * cifras que son cantidades y no dinero (no llevan $ ni se compactan).
 */
function Tile({ label, v, color, bg, conteo = false }) {
  const valor = Number(v || 0);
  const exacto = conteo
    ? fmt(valor)
    : `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(valor)}`;
  const compacto = conteo ? fmt(valor) : _fmtCompacto(valor);

  // Al compactar se pierde la cifra exacta; el tooltip la devuelve.
  const ayuda = (!conteo && Math.abs(valor) >= 1000) ? exacto : '';

  const contenido = (
    <Box sx={{
      p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
      height: '100%', display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', minWidth: 0, overflow: 'hidden', gap: 0.5,
      cursor: ayuda ? 'help' : 'default',
    }}>
      <Typography sx={{
        fontSize: { xs: 9, sm: 9, lg: 8.5 },
        color, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, lineHeight: 1.2, alignSelf: 'flex-start',
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: { xs: 15, sm: 15, md: 14, lg: 13, xl: 15 },
        fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums',
      }}>
        {compacto}
      </Typography>
    </Box>
  );

  return ayuda ? <Tooltip title={ayuda} arrow>{contenido}</Tooltip> : contenido;
}

